import {useNavigate} from "react-router-dom";
import {
  AllocatableLoadSettingsText,
  ContainerAction,
  ContainerStatusDataGrid,
  ContainerStatusFilterFormModal,
  ContainerStatusFilterFormValues,
  ContainerStatusQuickFilter,
  getFormattedDateTime,
  isDefaultFilter,
  ResizeableContent,
  TagDefinitionMultiSelect,
  ThemedPage,
  useApp,
  useContainerAction,
  useDownloadContainerStatusReport,
  useGetContainerStatuses,
} from "@ots-frontend/ots-ui";
import {
  ConnectedContainerDetailsPanel,
  ContainerStatusWorkspace,
  type ContainerStatusWorkspaceNotice,
  isPollStale,
  useContainerStatusDisplay,
} from "@ots-frontend/ots-ui-tw";
import {LoadingOverlay} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  adjustToTimeZone,
  allocatableLoadSettingsListSelector,
  cargoSubtype,
  ContainerStatusEntity,
  firstOrUndefined,
  startOfHour,
  TagDefinitionEntity,
  transportCategory,
  useSearchAllocatableLoadSettingsQuery,
  useTagDefinitionSearchQuery,
} from "@ots-frontend/ots-data";
import {useDisclosure, useFullscreen} from "@mantine/hooks";
import {baseQueryApi} from "../../store/api";
import {getContainerStatusPageFilter, getContainerStatusPageState} from "../../store/container-status-page/selectors";
import {useDispatch} from "react-redux";
import {setContainerStatusPageFilter, setContainerStatusPageStateCell} from "../../store/container-status-page/actions";
import {skipToken} from "@reduxjs/toolkit/query";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";

export type ContainerStatusPageProps = {
  // props here
}

export const ContainerStatusPage = (_props: ContainerStatusPageProps) => {
  const appCategory = useAppSelector(getAppCategory);
  const navigate = useNavigate();
  const filter = useAppSelector(getContainerStatusPageFilter)
  const gridState = useAppSelector(getContainerStatusPageState)

  const {ref: fullscreenRef, toggle: toggleFullscreen} = useFullscreen();
  const dataEditorRef = useRef<DataEditorRef>(null);
  const [gridControlsTarget, setGridControlsTarget] = useState<HTMLDivElement | null>(null);

  const [showSearch, setShowSearch] = useState(false);
  const onSearchClose = useCallback(() => setShowSearch(false), []);
  const [showTagFilter, setShowTagFilter] = useState(false);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const [filterOpened, filterHandler] = useDisclosure(false);
  const {display, onDisplayChange} = useContainerStatusDisplay();
  // The container whose tickets and levels are open beside the grid; looked up again after each refresh.
  const [openContainerId, setOpenContainerId] = useState<string>();
  const {t} = useTranslation(["translation", "common"]);
  const dispatch = useDispatch()
  const {regionId} = useApp()

  const {
    objectIds: taggedObjectIds,
  } = useTagDefinitionSearchQuery({
    tagDefinitions: filter.tagDefinitions,
    objectTypes: ["CONTAINER"]
  })

  const {
    containerStatuses,
    isLoading,
    isFetching,
    lastFetchedDttm,
    refetch
  } = useGetContainerStatuses({
    filter: filter,
    containerIds: taggedObjectIds,
  })

  const notices = useMemo(() => {
    const result: ContainerStatusWorkspaceNotice[] = []
    if (containerStatuses.some(status => status.metadata?.connection?.source === "LOCAL_TENANT_FALLBACK")) {
      result.push({
        id: "local",
        tone: "warning",
        message: t(
          "page.containerstatus.notice.local.tooltip",
          "Using local container records because a direct company connection is unavailable for some results."
        ),
      })
    }

    const measurementReasons = new Set(containerStatuses
      .map(status => status.metadata?.measurements?.omissionReason)
      .filter((reason): reason is NonNullable<typeof reason> => Boolean(reason)))

    if (measurementReasons.has("ENRICHMENT_FAILED")) {
      result.push({id: "readings", tone: "danger", message: t("page.containerstatus.notice.readings.failed", "Some tank readings could not be loaded. Refresh to try again.")})
    } else if (measurementReasons.has("CONNECTION_UNAVAILABLE")) {
      result.push({id: "readings", tone: "warning", message: t("page.containerstatus.notice.readings.unavailable", "Tank readings are unavailable for some containers.")})
    } else if (measurementReasons.has("NOT_REQUESTED")) {
      result.push({id: "readings", tone: "info", message: t("page.containerstatus.notice.readings.notincluded", "Tank readings were not included for some containers.")})
    }

    return result
  }, [containerStatuses, t])

  // restore the row the user last clicked
  const initialCell = useRef(gridState?.cell);
  useEffect(() => {
    const cell = initialCell.current;
    if (!cell) return;
    const timer = window.setTimeout(() => {
      dataEditorRef.current?.scrollTo(0, cell[1], "both", 0, 0, {vAlign: "start", hAlign: "start"});
      setSelection(previous => ({...previous, current: {
        cell: [0, cell[1]], range: {x: 0, y: cell[1], width: 1, height: 1}, rangeStack: [],
      }}));
    }, 200);
    return () => window.clearTimeout(timer);
  }, []);

  const {
    onMultiContainerActionClick
  } = useContainerAction()

  const {
    downloadContainerStatusReport,
  } = useDownloadContainerStatusReport({baseQueryApi})

  const selectAllocatableLoadSettings = useMemo(() => {
    return allocatableLoadSettingsListSelector();
  }, []);

  const {allocatableLoadSetting} = useSearchAllocatableLoadSettingsQuery(regionId ? {
    categories: [appCategory.code],
    objectIds: [regionId],
    dttm: adjustToTimeZone(startOfHour(new Date())).getTime(),
  } : skipToken, {
    selectFromResult: (result) => ({
      allocatableLoadSetting: firstOrUndefined(selectAllocatableLoadSettings(result)),
      isLoading: result.isLoading
    })
  });

  const cargoSubtypes = useMemo(() => {
    if (appCategory === transportCategory.water) {
      return new Set([cargoSubtype.water, cargoSubtype.oil])
    } else {
      return new Set([cargoSubtype.oil, cargoSubtype.water])
    }
  }, [appCategory])

  const onActionClick = useCallback((data: ContainerStatusEntity[], action: ContainerAction) => {
    onMultiContainerActionClick(data, action)
  }, [onMultiContainerActionClick])

  const onFilterSubmit = useCallback((filter: ContainerStatusFilterFormValues) => {
    dispatch(setContainerStatusPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  const onQuickFilter = useCallback((quickFilter: ContainerStatusFilterFormValues) => {
    dispatch(setContainerStatusPageFilter({...filter, ...quickFilter}))
  }, [dispatch, filter])

  const onTagDefinitionChange = useCallback((defs: TagDefinitionEntity[]) => {
    dispatch(setContainerStatusPageFilter({
      ...filter,
      tagDefinitions: defs.map((def) => ({id: def.id, key: def.key})),
    }))
  }, [dispatch, filter])

  const onExportClick = useCallback(() => {
    downloadContainerStatusReport(appCategory)
  }, [downloadContainerStatusReport, appCategory])

  const openContainer = useMemo(
    () => containerStatuses.find((status) => status.containerId === openContainerId),
    [containerStatuses, openContainerId]
  )
  const onQuickView = useCallback((status: ContainerStatusEntity) => setOpenContainerId(status.containerId), [])

  const onCellClicked = useCallback((cell: Item, _event: CellClickedEventArgs) => {
    dispatch(setContainerStatusPageStateCell([cell[0], cell[1]]))
  }, [dispatch])

  return (
    <ThemedPage>
      {() => (
        <ResizeableContent>
          {(size) => (
            <div style={{height: size.height, width: size.width, minWidth: 0}}>
              <ContainerStatusWorkspace
                containerCount={containerStatuses.length}
                lastFetched={lastFetchedDttm ? getFormattedDateTime(lastFetchedDttm, true, true) : undefined}
                isRefreshing={isFetching}
                showSearch={showSearch}
                onSearchClick={() => setShowSearch((value) => !value)}
                filterActive={!isDefaultFilter(filter)}
                onFilterClick={filterHandler.open}
                tagFilterActive={showTagFilter || filter.tagDefinitions?.length > 0}
                onTagFilterClick={() => setShowTagFilter((value) => !value)}
                onRefresh={() => refetch()}
                onExport={onExportClick}
                onFullscreen={toggleFullscreen}
                notices={notices}
                settingsSummary={allocatableLoadSetting &&
                  <AllocatableLoadSettingsText data={allocatableLoadSetting} separator={"\n"} style={{whiteSpace: "pre-wrap"}}/>}
                gridControlsRef={setGridControlsTarget}
                display={display}
                onDisplayChange={onDisplayChange}
                aside={openContainer && (
                  <ConnectedContainerDetailsPanel
                    className="h-full"
                    container={openContainer}
                    stalePoll={isPollStale(openContainer, display, {now: Date.now(), category: appCategory})}
                    ticketParty="hauler"
                    onOpenTicket={(tta) => navigate(`/trucking/tickets/profile?id=${tta.id}${tta.database ? `&database=${tta.database}` : ""}`)}
                    onClose={() => setOpenContainerId(undefined)}/>
                )}
                context={
                  <>
                    <ContainerStatusQuickFilter
                      showDispatchFilters={false}
                      value={filter}
                      onChange={onQuickFilter}/>
                    {showTagFilter &&
                      <TagDefinitionMultiSelect
                        size="xs"
                        mt="xs"
                        withinPortal={true}
                        onEntityChange={onTagDefinitionChange}
                        value={filter.tagDefinitions?.map((def) => def.id)}
                      />
                    }
                  </>
                }
              >
                <LoadingOverlay visible={isLoading} overlayBlur={2}/>
                <div ref={fullscreenRef} style={{height: "100%", minHeight: 0}}>
                  <ContainerStatusDataGrid
                    ref={dataEditorRef}
                    height="100%"
                    controlsTarget={gridControlsTarget}
                    showSearch={showSearch}
                    onSearchClose={onSearchClose}
                    actions={[
                      "TOGGLE_DNU",
                      "TOGGLE_DO_NOT_DISPATCH",
                      "TOGGLE_DISPATCH_IGNORE_MAX_NON_PRIMARY_VOLUME",
                      "DISPATCH_REQUEST",
                      "TOGGLE_LACT_METER_DNU",
                      "TOGGLE_SITE_TRANSPORT_VIA_PIPELINE",
                      "TOGGLE_SITE_TRANSPORT_OIL_VIA_PIPELINE",
                      "TOGGLE_REJECTION",
                      "MANAGE_TAGS",
                    ]}
                    onActionClick={onActionClick}
                    onCellClicked={onCellClicked}
                    gridSelection={selection}
                    onGridSelectionChange={setSelection}
                    colWidths={gridState.widths}
                    sort={gridState.sort}
                    cargoSubtypes={cargoSubtypes}
                    data={containerStatuses}
                    display={display}
                    transportCategory={appCategory}
                    onQuickView={onQuickView}
                    colOptions={{
                      showSiteVolumeCol: true,
                    }}
                  />
                </div>
              </ContainerStatusWorkspace>

              <ContainerStatusFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />
            </div>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
